/**
 * NIS2 Article 23 incident reporting — strings.
 *
 * SAME PATTERN AS EVIDENCE_STRINGS / CONTENT_WATCH_STRINGS
 *   INCIDENT_STRINGS     English; also the inline `defaultValue` of every
 *                        lookup, so the screen renders correctly before the
 *                        locale files are updated.
 *   INCIDENT_STRINGS_EL  Greek.
 * `registerIncidentTranslations()` adds both to the running i18n instance
 * without touching i18n/index.ts or the locale JSON, so the parity guard —
 * which reads the JSON — is unaffected. Once merged into en.json and el.json
 * the registration is redundant but harmless: `addResources` never overwrites
 * a key that is already there.
 *
 * ---------------------------------------------------------------------------
 *  THE WORDING THAT MATTERS MOST
 * ---------------------------------------------------------------------------
 * `incident.notFiled.*` is the product telling the customer, repeatedly and in
 * plain words, that WE DO NOT FILE ANYTHING. It appears on the incident screen,
 * above every report editor, inside the submission dialog, and in every export.
 * A customer who believes we notified their authority and we did not is the
 * worst outcome this feature can produce, so this is deliberately blunt,
 * deliberately repeated, and must not be shortened into a tooltip.
 *
 * `incident.national.*` is the second honest admission: much of NIS2 reporting
 * depends on national transposition — who you report to, through what channel,
 * and the thresholds that make an incident "significant". Where we do not know,
 * we say we do not know instead of asserting one answer.
 */

import i18n from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export const INCIDENT_STRINGS = {
  // --- Navigation (for whoever adds the Shell entry) ------------------------
  'nav.incidents': 'Incidents',
  'nav.shortIncidents': 'Incidents',

  // --- Register ------------------------------------------------------------
  'incident.title': 'Incident reporting',
  'incident.subtitle':
    'NIS2 Article 23 staged reporting. Declare an incident, watch the clocks, prepare each report, and record what you sent.',
  'incident.declare': 'Declare an incident',
  'incident.loadFailed': 'Could not load the incident register.',
  'incident.retry': 'Try again',
  'incident.empty.title': 'No incidents recorded',
  'incident.empty.body':
    'If something is happening now, declare it. You need three things: when you became aware, one sentence about what happened, and how severe you think it is.',
  'incident.filter.all': 'All',
  'incident.count_one': '{{count}} incident',
  'incident.count_other': '{{count}} incidents',
  'incident.missedDeadline': 'A deadline has passed',
  'incident.openIncident': 'Open',

  // --- Declaration ---------------------------------------------------------
  'incident.declare.title': 'Declare an incident',
  'incident.declare.intro':
    'Three fields now. Everything else can wait — you can add it at any time, and nothing here is locked.',
  'incident.declare.awareAt': 'When did you become aware?',
  'incident.declare.awareAtHint':
    'This starts every Article 23 clock. Use the moment someone in your organisation first knew — not when it was confirmed, and not now if that is later.',
  'incident.declare.awareAtNow': 'Just now',
  'incident.declare.title_field': 'What happened, in one sentence',
  'incident.declare.titleHint': 'You can write the full account later.',
  'incident.declare.severity': 'How severe does it look?',
  'incident.declare.severityHint':
    'Your own judgement, changeable at any time. This is NOT the same as deciding the incident is “significant” under Article 23(3) — that question is asked separately.',
  'incident.declare.description': 'Anything else you already know (optional)',
  'incident.declare.submit': 'Declare',
  'incident.declare.futureError':
    'That is in the future. The awareness time must be now or earlier.',

  // --- Severities and statuses ---------------------------------------------
  'incident.severity.LOW': 'Low',
  'incident.severity.MEDIUM': 'Medium',
  'incident.severity.HIGH': 'High',
  'incident.severity.CRITICAL': 'Critical',
  'incident.status.OPEN': 'Open',
  'incident.status.CONTAINED': 'Contained',
  'incident.status.RESOLVED': 'Resolved',
  'incident.status.CLOSED': 'Closed',
  'incident.tristate.YES': 'Yes',
  'incident.tristate.NO': 'No',
  'incident.tristate.UNKNOWN': 'Not yet known',

  // --- Clocks --------------------------------------------------------------
  'incident.clocks.title': 'Deadlines',
  'incident.clocks.awareAt': 'Became aware {{when}}',
  'incident.clocks.basis':
    'Every clock below runs from that moment. “Without undue delay” is the actual obligation — 24 and 72 hours are the Directive’s outer limits, not a budget to spend.',
  'incident.stage.EARLY_WARNING': 'Early warning',
  'incident.stage.NOTIFICATION': 'Incident notification',
  'incident.stage.INTERMEDIATE': 'Intermediate report',
  'incident.stage.FINAL': 'Final report',
  'incident.stage.PROGRESS': 'Progress report',
  'incident.stageLimit.EARLY_WARNING': 'within 24 hours of becoming aware',
  'incident.stageLimit.NOTIFICATION': 'within 72 hours of becoming aware',
  'incident.stageLimit.INTERMEDIATE': 'when the CSIRT or authority asks for one',
  'incident.stageLimit.FINAL': 'within one month of submitting the notification',
  'incident.stageLimit.PROGRESS': 'when the incident is still ongoing at the final report',
  'incident.stageCite.EARLY_WARNING': 'Article 23(4)(a)',
  'incident.stageCite.NOTIFICATION': 'Article 23(4)(b)',
  'incident.stageCite.INTERMEDIATE': 'Article 23(4)(c)',
  'incident.stageCite.FINAL': 'Article 23(4)(d)',
  'incident.stageCite.PROGRESS': 'Article 23(4)(e)',

  'incident.deadline.remaining': '{{duration}} left',
  'incident.deadline.dueAt': 'Due {{when}}',
  'incident.deadline.overdue': 'DEADLINE PASSED',
  'incident.deadline.overdueDetail':
    'This fell due {{when}} — {{duration}} ago — and nothing has been recorded as sent. The obligation does not expire with the deadline: send it now. A late report is still required, and the delay is something your authority will ask you to account for.',
  'incident.deadline.submittedOnTime': 'Sent within the limit',
  'incident.deadline.submittedLate': 'Sent {{duration}} late',
  'incident.deadline.submittedLateDetail':
    'Recorded as sent {{when}}, which is {{duration}} after the limit. This is kept in the record deliberately — it is a fact you may have to explain, not one to bury.',
  'incident.deadline.notScheduled': 'Not yet scheduled',
  'incident.deadline.notScheduledDetail':
    'The one-month period for the final report runs from the moment you submit the 72-hour notification — not from when you became aware. Until that notification is recorded as sent, this date cannot be calculated, so we do not guess at one.',
  'incident.deadline.onRequest': 'Only on request',
  'incident.deadline.onRequestDetail':
    'Article 23(4)(c) sets no deadline for an intermediate report. It exists when a CSIRT or competent authority asks for one, so we do not invent a clock for it.',
  'incident.deadline.monthNote':
    'Calculated as one calendar month. Your authority’s own reckoning governs.',

  'incident.nextAction.title': 'Do this next',
  'incident.nextAction.overdue': 'Send the {{stage}} now — it is already past the limit.',
  'incident.nextAction.open': 'Prepare the {{stage}}. {{duration}} left.',
  'incident.nextAction.none': 'Nothing is outstanding against the Article 23 deadlines.',

  // --- The disclaimer. The most important strings in this file. ------------
  'incident.notFiled.banner':
    'OpenComply does not send anything to any authority. We help you prepare the report and record what you sent; actually sending it, through your national authority’s own channel, is yours to do.',
  'incident.notFiled.short':
    'We do not file this for you. Export it, send it yourself, then record it here.',
  'incident.notFiled.confirmTitle': 'Record what you sent',
  'incident.notFiled.confirmBody':
    'This records that YOU sent this report. It does not send anything — OpenComply has no connection to any CSIRT or competent authority and cannot submit on your behalf.',
  'incident.notFiled.acknowledge':
    'I understand OpenComply is not filing this. I have sent it myself, or I am recording a submission I made.',

  // --- National variation --------------------------------------------------
  'incident.national.title': 'What depends on your Member State',
  'incident.national.body':
    'Who you report to, through what channel and on what form are set by your Member State’s transposition of the Directive, and for some sectors by rules of their own. The thresholds that make an incident “significant” vary the same way. The deadlines shown here are the Directive’s outer limits — your national rules may be stricter. We deliberately do not pre-fill an authority or a threshold we cannot know.',
  'incident.national.authority': 'Authority or CSIRT you report to',
  'incident.national.authorityHint':
    'We cannot know this — it depends on your Member State and your sector. Recording it here saves you typing it into each report.',
  'incident.national.contact': 'How you reach them (portal address, e-mail, phone)',

  // --- Assessment fields ---------------------------------------------------
  'incident.assess.title': 'Assessment',
  'incident.assess.statusLabel': 'Where the incident stands',
  'incident.assess.statusHint':
    'The incident\u2019s own lifecycle. It is independent of whether its reports have been sent \u2014 closing an incident never closes a reporting obligation.',
  'incident.assess.significant': 'Do you consider this a significant incident under Article 23(3)?',
  'incident.assess.significantHint':
    'This is your decision, not ours. Article 23(3) gives qualitative criteria; the numbers that go with them are set nationally and for some entity types by sectoral rules, so we do not calculate significance for you.',
  'incident.assess.significanceNote': 'Why you reached that view',
  'incident.assess.malicious': 'Suspected to be caused by an unlawful or malicious act?',
  'incident.assess.maliciousHint': 'Article 23(4)(a) asks for this in the early warning. “Not yet known” is a legitimate answer at this stage.',
  'incident.assess.crossBorder': 'Could it have cross-border impact?',
  'incident.assess.recipients': 'Recipients of your service notified (Article 23(1))',
  'incident.assess.recipientsHint':
    'Where an incident is likely to adversely affect your service recipients, they are notified too. The Directive sets no clock on this, so we record it rather than count it down.',
  'incident.assess.recipientsNote': 'What you told them',
  'incident.assess.ongoing': 'Still being handled when the final report falls due (Article 23(4)(e))',

  // --- Report editor -------------------------------------------------------
  'incident.report.open': 'Prepare',
  'incident.report.openDraft': 'Continue draft',
  'incident.report.view': 'View',
  'incident.report.draft': 'Draft',
  'incident.report.submitted': 'Recorded as sent',
  'incident.report.notStarted': 'Not started',
  'incident.report.editorTitle': '{{stage}} — {{reference}}',
  'incident.report.savedAt': 'Saved {{when}}',
  'incident.report.saving': 'Saving…',
  'incident.report.saveFailed':
    'Could not save to the server. Your typing is kept in this browser and will be sent when the connection returns — do not close this tab if you can avoid it.',
  'incident.report.restored':
    'Restored unsaved changes kept in this browser from {{when}}.',
  'incident.report.lockedTitle': 'This report is recorded as sent',
  'incident.report.lockedBody':
    'It is no longer editable, so the record cannot drift from what your authority holds. If something has changed, that is what the intermediate and final reports are for.',

  'incident.field.summary': 'What happened',
  'incident.field.summaryHint': 'Enough for the reader to understand the event. For the early warning this can be two sentences.',
  'incident.field.severityAssessment': 'Initial assessment of severity',
  'incident.field.severityAssessmentHint': 'Article 23(4)(b) asks for this in the 72-hour notification.',
  'incident.field.impact': 'Impact',
  'incident.field.impactHint': 'Operational disruption, financial loss, harm to others.',
  'incident.field.affectedServices': 'Affected services and recipients',
  'incident.field.indicatorsOfCompromise': 'Indicators of compromise',
  'incident.field.indicatorsOfCompromiseHint': 'Article 23(4)(b) asks for these “where available”. Leave it empty if you have none yet.',
  'incident.field.rootCause': 'Type of threat, or likely root cause',
  'incident.field.rootCauseHint': 'Article 23(4)(d)(ii).',
  'incident.field.mitigation': 'Mitigation applied and ongoing',
  'incident.field.mitigationHint': 'Article 23(4)(d)(iii).',
  'incident.field.crossBorderDetail': 'Cross-border impact',
  'incident.field.crossBorderDetailHint': 'Where applicable — Article 23(4)(a) and (d)(iv).',
  'incident.field.ongoingStatus': 'Current status of handling',
  'incident.field.additionalInformation': 'Anything else your authority’s form asks for',
  'incident.field.additionalInformationHint':
    'The fields above follow what Article 23(4) describes. Your national form may ask for more — put it here.',
  'incident.field.contactName': 'Contact name',
  'incident.field.contactEmail': 'Contact e-mail',
  'incident.field.contactPhone': 'Contact telephone',
  'incident.field.contactPrefilled': 'Pre-filled from your account and organisation. Change it if someone else is on call.',

  // --- Warnings, never blocks ---------------------------------------------
  'incident.warnings.title': 'Still empty',
  'incident.warnings.body':
    'These are usually wanted at this stage. They do not stop you recording a submission — at hour 23 an incomplete early warning beats a perfect unsent one.',
  'incident.warnings.none': 'Everything this stage usually asks for has something in it.',

  // --- Export --------------------------------------------------------------
  'incident.export.title': 'Export',
  'incident.export.body':
    'Take this to your authority’s own portal, e-mail or form. The text version is laid out to paste straight into a web form.',
  'incident.export.pdf': 'Download PDF',
  'incident.export.txt': 'Download text',
  'incident.export.csv': 'Timeline (CSV)',
  'incident.export.failed': 'The export could not be produced.',

  // --- Recording a submission ---------------------------------------------
  'incident.submit.action': 'Record that you sent this',
  'incident.submit.when': 'When did you send it?',
  'incident.submit.whenHint': 'Backdate it if you sent it earlier — this records what happened, not what this screen did.',
  'incident.submit.to': 'Who did you send it to?',
  'incident.submit.toHint': 'The CSIRT or competent authority, as you addressed it.',
  'incident.submit.via': 'How did you send it?',
  'incident.submit.via.PORTAL': 'Authority’s portal',
  'incident.submit.via.EMAIL': 'E-mail',
  'incident.submit.via.PHONE': 'Telephone',
  'incident.submit.via.POST': 'Post',
  'incident.submit.via.OTHER': 'Other',
  'incident.submit.reference': 'Reference they gave you (optional)',
  'incident.submit.lateness': 'Note on the timing (optional)',
  'incident.submit.latenessHint':
    'This is past the deadline. A short note on why is worth writing now, while you remember — your authority is likely to ask.',
  'incident.submit.confirm': 'Record it',
  'incident.submit.failed': 'Could not record the submission.',

  // --- Timeline ------------------------------------------------------------
  'incident.timeline.title': 'Timeline',
  'incident.timeline.body':
    'What was done, when and by whom. This is your own record of how you handled the incident, and it is printed into the reports you export.',
  'incident.timeline.add': 'Add an entry',
  'incident.timeline.when': 'When did it happen?',
  'incident.timeline.whenHint': 'Not when you are typing it — when it happened.',
  'incident.timeline.summary': 'What happened',
  'incident.timeline.detail': 'More detail (optional)',
  'incident.timeline.save': 'Add',
  'incident.timeline.by': 'by {{who}}',
  'incident.timeline.system': 'recorded automatically',
  'incident.timeline.empty': 'Nothing recorded yet.',
  'incident.timeline.kind.DECLARED': 'Declared',
  'incident.timeline.kind.NOTE': 'Note',
  'incident.timeline.kind.STATUS_CHANGED': 'Status',
  'incident.timeline.kind.SEVERITY_CHANGED': 'Severity',
  'incident.timeline.kind.DETAILS_UPDATED': 'Details',
  'incident.timeline.kind.REPORT_STARTED': 'Draft started',
  'incident.timeline.kind.REPORT_SUBMITTED': 'Report sent',
  'incident.timeline.kind.RECIPIENTS_NOTIFIED': 'Recipients told',
  'incident.timeline.kind.CONTROL_LINKED': 'Linked to a control',

  // --- Evidence for a control ---------------------------------------------
  'incident.controls.title': 'Evidence for your controls',
  'incident.controls.body':
    'A handled incident demonstrates that your incident-handling and reporting controls work. Link it, and the record shows up as evidence against those controls. This never creates a gap — an incident is an event, not a shortfall.',
  'incident.controls.add': 'Link a control',
  'incident.controls.suggested': 'Suggested',
  'incident.controls.none': 'Not linked to any control yet.',
  'incident.controls.remove': 'Unlink',
  'incident.controls.chooseLabel': 'Which control does this incident demonstrate?',

  // --- Errors --------------------------------------------------------------
  'incident.error.INCIDENT_NOT_FOUND': 'That incident does not exist, or is not yours.',
  'incident.error.INCIDENT_REPORT_NOT_FOUND': 'That report has not been started yet.',
  'incident.error.INCIDENT_REPORT_ALREADY_SUBMITTED':
    'That report is already recorded as sent, so it can no longer be changed.',
  'incident.error.INCIDENT_AWARE_AT_IN_FUTURE':
    'The awareness time is in the future. It must be now or earlier.',
  'incident.error.ORGANIZATION_CONTROL_NOT_FOUND': 'That control is not in your library.',
} as const;

export type IncidentKey = keyof typeof INCIDENT_STRINGS;
/** Keys with an `_one`/`_other` family, for the plural-aware lookup. */
type IncidentPluralKey = 'incident.count';

export const INCIDENT_STRINGS_EL: Record<IncidentKey, string> = {
  'nav.incidents': 'Περιστατικά',
  'nav.shortIncidents': 'Περιστατικά',

  'incident.title': 'Αναφορά περιστατικών',
  'incident.subtitle':
    'Κλιμακωτή αναφορά κατά το άρθρο 23 NIS2. Δηλώστε περιστατικό, παρακολουθήστε τα χρονόμετρα, ετοιμάστε κάθε αναφορά και καταγράψτε τι στείλατε.',
  'incident.declare': 'Δήλωση περιστατικού',
  'incident.loadFailed': 'Δεν ήταν δυνατή η φόρτωση του μητρώου περιστατικών.',
  'incident.retry': 'Δοκιμάστε ξανά',
  'incident.empty.title': 'Δεν έχει καταγραφεί περιστατικό',
  'incident.empty.body':
    'Αν συμβαίνει κάτι τώρα, δηλώστε το. Χρειάζονται τρία πράγματα: πότε το αντιληφθήκατε, μία πρόταση για το τι έγινε, και πόσο σοβαρό το κρίνετε.',
  'incident.filter.all': 'Όλα',
  'incident.count_one': '{{count}} περιστατικό',
  'incident.count_other': '{{count}} περιστατικά',
  'incident.missedDeadline': 'Έχει παρέλθει προθεσμία',
  'incident.openIncident': 'Άνοιγμα',

  'incident.declare.title': 'Δήλωση περιστατικού',
  'incident.declare.intro':
    'Τρία πεδία τώρα. Όλα τα υπόλοιπα μπορούν να περιμένουν — μπορείτε να τα προσθέσετε οποτεδήποτε, και τίποτα εδώ δεν κλειδώνει.',
  'incident.declare.awareAt': 'Πότε το αντιληφθήκατε;',
  'incident.declare.awareAtHint':
    'Από εδώ ξεκινούν όλα τα χρονόμετρα του άρθρου 23. Βάλτε τη στιγμή που κάποιος στον οργανισμό σας το γνώριζε πρώτη φορά — όχι πότε επιβεβαιώθηκε, ούτε τώρα αν αυτό είναι αργότερα.',
  'incident.declare.awareAtNow': 'Μόλις τώρα',
  'incident.declare.title_field': 'Τι συνέβη, σε μία πρόταση',
  'incident.declare.titleHint': 'Την πλήρη περιγραφή μπορείτε να τη γράψετε αργότερα.',
  'incident.declare.severity': 'Πόσο σοβαρό φαίνεται;',
  'incident.declare.severityHint':
    'Δική σας κρίση, αλλάζει οποτεδήποτε. ΔΕΝ ταυτίζεται με την κρίση ότι το περιστατικό είναι «σημαντικό» κατά το άρθρο 23(3) — αυτό ρωτιέται χωριστά.',
  'incident.declare.description': 'Ό,τι άλλο ήδη γνωρίζετε (προαιρετικό)',
  'incident.declare.submit': 'Δήλωση',
  'incident.declare.futureError': 'Αυτό είναι στο μέλλον. Η ώρα αντίληψης πρέπει να είναι τώρα ή νωρίτερα.',

  'incident.severity.LOW': 'Χαμηλή',
  'incident.severity.MEDIUM': 'Μέτρια',
  'incident.severity.HIGH': 'Υψηλή',
  'incident.severity.CRITICAL': 'Κρίσιμη',
  'incident.status.OPEN': 'Ανοικτό',
  'incident.status.CONTAINED': 'Περιορισμένο',
  'incident.status.RESOLVED': 'Επιλύθηκε',
  'incident.status.CLOSED': 'Κλειστό',
  'incident.tristate.YES': 'Ναι',
  'incident.tristate.NO': 'Όχι',
  'incident.tristate.UNKNOWN': 'Άγνωστο ακόμη',

  'incident.clocks.title': 'Προθεσμίες',
  'incident.clocks.awareAt': 'Αντίληψη {{when}}',
  'incident.clocks.basis':
    'Κάθε χρονόμετρο παρακάτω μετρά από εκείνη τη στιγμή. Η πραγματική υποχρέωση είναι «χωρίς αδικαιολόγητη καθυστέρηση» — οι 24 και οι 72 ώρες είναι τα εξωτερικά όρια της Οδηγίας, όχι χρόνος προς κατανάλωση.',
  'incident.stage.EARLY_WARNING': 'Έγκαιρη προειδοποίηση',
  'incident.stage.NOTIFICATION': 'Κοινοποίηση περιστατικού',
  'incident.stage.INTERMEDIATE': 'Ενδιάμεση έκθεση',
  'incident.stage.FINAL': 'Τελική έκθεση',
  'incident.stage.PROGRESS': 'Έκθεση προόδου',
  'incident.stageLimit.EARLY_WARNING': 'εντός 24 ωρών από την αντίληψη',
  'incident.stageLimit.NOTIFICATION': 'εντός 72 ωρών από την αντίληψη',
  'incident.stageLimit.INTERMEDIATE': 'όταν το ζητήσει το CSIRT ή η αρχή',
  'incident.stageLimit.FINAL': 'εντός ενός μηνός από την υποβολή της κοινοποίησης',
  'incident.stageLimit.PROGRESS': 'όταν το περιστατικό είναι ακόμη σε εξέλιξη κατά την τελική έκθεση',
  'incident.stageCite.EARLY_WARNING': 'Άρθρο 23(4)(α)',
  'incident.stageCite.NOTIFICATION': 'Άρθρο 23(4)(β)',
  'incident.stageCite.INTERMEDIATE': 'Άρθρο 23(4)(γ)',
  'incident.stageCite.FINAL': 'Άρθρο 23(4)(δ)',
  'incident.stageCite.PROGRESS': 'Άρθρο 23(4)(ε)',

  'incident.deadline.remaining': 'απομένουν {{duration}}',
  'incident.deadline.dueAt': 'Λήγει {{when}}',
  'incident.deadline.overdue': 'Η ΠΡΟΘΕΣΜΙΑ ΠΑΡΗΛΘΕ',
  'incident.deadline.overdueDetail':
    'Έληξε {{when}} — πριν από {{duration}} — και δεν έχει καταγραφεί αποστολή. Η υποχρέωση δεν παύει με την προθεσμία: στείλτε την τώρα. Η εκπρόθεσμη αναφορά εξακολουθεί να οφείλεται, και η καθυστέρηση είναι κάτι που η αρχή σας θα σας ζητήσει να αιτιολογήσετε.',
  'incident.deadline.submittedOnTime': 'Στάλθηκε εντός του ορίου',
  'incident.deadline.submittedLate': 'Στάλθηκε {{duration}} εκπρόθεσμα',
  'incident.deadline.submittedLateDetail':
    'Καταγράφηκε ως σταλμένη {{when}}, δηλαδή {{duration}} μετά το όριο. Παραμένει στο αρχείο σκοπίμως — είναι γεγονός που ίσως χρειαστεί να εξηγήσετε, όχι να το κρύψετε.',
  'incident.deadline.notScheduled': 'Δεν έχει οριστεί ακόμη',
  'incident.deadline.notScheduledDetail':
    'Ο ένας μήνας για την τελική έκθεση μετρά από τη στιγμή που υποβάλλετε την κοινοποίηση των 72 ωρών — όχι από την αντίληψη. Μέχρι να καταγραφεί αυτή η κοινοποίηση ως σταλμένη, η ημερομηνία δεν υπολογίζεται, και δεν την μαντεύουμε.',
  'incident.deadline.onRequest': 'Μόνο κατόπιν αιτήματος',
  'incident.deadline.onRequestDetail':
    'Το άρθρο 23(4)(γ) δεν θέτει προθεσμία για ενδιάμεση έκθεση. Υπάρχει όταν το ζητήσει CSIRT ή αρμόδια αρχή, οπότε δεν επινοούμε χρονόμετρο.',
  'incident.deadline.monthNote':
    'Υπολογίζεται ως ένας ημερολογιακός μήνας. Υπερισχύει ο υπολογισμός της δικής σας αρχής.',

  'incident.nextAction.title': 'Κάντε αυτό μετά',
  'incident.nextAction.overdue': 'Στείλτε τώρα: {{stage}} — έχει ήδη περάσει το όριο.',
  'incident.nextAction.open': 'Ετοιμάστε: {{stage}}. Απομένουν {{duration}}.',
  'incident.nextAction.none': 'Δεν εκκρεμεί τίποτα ως προς τις προθεσμίες του άρθρου 23.',

  'incident.notFiled.banner':
    'Το OpenComply δεν στέλνει τίποτα σε καμία αρχή. Σας βοηθά να ετοιμάσετε την αναφορά και να καταγράψετε τι στείλατε· η ίδια η αποστολή, μέσω του καναλιού της εθνικής σας αρχής, είναι δική σας ευθύνη.',
  'incident.notFiled.short':
    'Δεν την υποβάλλουμε εμείς για εσάς. Εξάγετέ την, στείλτε την μόνοι σας, και μετά καταγράψτε την εδώ.',
  'incident.notFiled.confirmTitle': 'Καταγραφή όσων στείλατε',
  'incident.notFiled.confirmBody':
    'Αυτό καταγράφει ότι ΕΣΕΙΣ στείλατε την αναφορά. Δεν στέλνει τίποτα — το OpenComply δεν έχει σύνδεση με κανένα CSIRT ή αρμόδια αρχή και δεν μπορεί να υποβάλει για λογαριασμό σας.',
  'incident.notFiled.acknowledge':
    'Κατανοώ ότι το OpenComply δεν υποβάλλει αυτή την αναφορά. Την έστειλα ο ίδιος, ή καταγράφω υποβολή που έκανα.',

  'incident.national.title': 'Τι εξαρτάται από το κράτος μέλος σας',
  'incident.national.body':
    'Σε ποιον αναφέρεστε, μέσω ποιου καναλιού και με ποια φόρμα ορίζονται από την ενσωμάτωση της Οδηγίας στο κράτος μέλος σας, και για ορισμένους τομείς από ειδικούς κανόνες. Τα κατώφλια που καθιστούν ένα περιστατικό «σημαντικό» ποικίλλουν ομοίως. Οι προθεσμίες εδώ είναι τα εξωτερικά όρια της Οδηγίας — οι εθνικοί σας κανόνες μπορεί να είναι αυστηρότεροι. Σκοπίμως δεν προσυμπληρώνουμε αρχή ή κατώφλι που δεν μπορούμε να γνωρίζουμε.',
  'incident.national.authority': 'Αρχή ή CSIRT στο οποίο αναφέρεστε',
  'incident.national.authorityHint':
    'Δεν μπορούμε να το γνωρίζουμε — εξαρτάται από το κράτος μέλος και τον τομέα σας. Καταγράφοντάς το εδώ γλιτώνετε την πληκτρολόγηση σε κάθε αναφορά.',
  'incident.national.contact': 'Πώς επικοινωνείτε (διεύθυνση πύλης, e-mail, τηλέφωνο)',

  'incident.assess.title': 'Αξιολόγηση',
  'incident.assess.statusLabel': 'Πού βρίσκεται το περιστατικό',
  'incident.assess.statusHint':
    'Ο κύκλος ζωής του ίδιου του περιστατικού. Είναι ανεξάρτητος από το αν έχουν σταλεί οι αναφορές του — το κλείσιμο ενός περιστατικού δεν κλείνει ποτέ υποχρέωση αναφοράς.',
  'incident.assess.significant': 'Θεωρείτε ότι πρόκειται για σημαντικό περιστατικό κατά το άρθρο 23(3);',
  'incident.assess.significantHint':
    'Είναι δική σας απόφαση, όχι δική μας. Το άρθρο 23(3) δίνει ποιοτικά κριτήρια· τα αντίστοιχα μεγέθη ορίζονται εθνικά και, για ορισμένες κατηγορίες οντοτήτων, από τομεακούς κανόνες, γι’ αυτό δεν υπολογίζουμε εμείς τη σημαντικότητα.',
  'incident.assess.significanceNote': 'Γιατί καταλήξατε σε αυτή την κρίση',
  'incident.assess.malicious': 'Υπάρχει υπόνοια παράνομης ή κακόβουλης πράξης;',
  'incident.assess.maliciousHint': 'Το άρθρο 23(4)(α) το ζητά στην έγκαιρη προειδοποίηση. Το «άγνωστο ακόμη» είναι θεμιτή απάντηση σε αυτό το στάδιο.',
  'incident.assess.crossBorder': 'Μπορεί να έχει διασυνοριακό αντίκτυπο;',
  'incident.assess.recipients': 'Ενημερώθηκαν οι αποδέκτες της υπηρεσίας (άρθρο 23(1))',
  'incident.assess.recipientsHint':
    'Όταν ένα περιστατικό ενδέχεται να επηρεάσει δυσμενώς τους αποδέκτες της υπηρεσίας σας, ενημερώνονται και αυτοί. Η Οδηγία δεν θέτει προθεσμία, οπότε το καταγράφουμε αντί να το μετράμε αντίστροφα.',
  'incident.assess.recipientsNote': 'Τι τους είπατε',
  'incident.assess.ongoing': 'Σε εξέλιξη κατά τη λήξη της τελικής έκθεσης (άρθρο 23(4)(ε))',

  'incident.report.open': 'Ετοιμασία',
  'incident.report.openDraft': 'Συνέχεια προσχεδίου',
  'incident.report.view': 'Προβολή',
  'incident.report.draft': 'Προσχέδιο',
  'incident.report.submitted': 'Καταγράφηκε ως σταλμένη',
  'incident.report.notStarted': 'Δεν ξεκίνησε',
  'incident.report.editorTitle': '{{stage}} — {{reference}}',
  'incident.report.savedAt': 'Αποθηκεύτηκε {{when}}',
  'incident.report.saving': 'Αποθήκευση…',
  'incident.report.saveFailed':
    'Δεν ήταν δυνατή η αποθήκευση στον διακομιστή. Ό,τι γράφετε φυλάσσεται σε αυτό το πρόγραμμα περιήγησης και θα σταλεί μόλις επανέλθει η σύνδεση — αποφύγετε να κλείσετε αυτή την καρτέλα.',
  'incident.report.restored': 'Αποκαταστάθηκαν μη αποθηκευμένες αλλαγές από αυτό το πρόγραμμα περιήγησης, από {{when}}.',
  'incident.report.lockedTitle': 'Η αναφορά είναι καταγεγραμμένη ως σταλμένη',
  'incident.report.lockedBody':
    'Δεν είναι πλέον επεξεργάσιμη, ώστε το αρχείο να μην αποκλίνει από αυτό που κατέχει η αρχή σας. Αν κάτι άλλαξε, γι’ αυτό υπάρχουν η ενδιάμεση και η τελική έκθεση.',

  'incident.field.summary': 'Τι συνέβη',
  'incident.field.summaryHint': 'Όσα αρκούν για να καταλάβει ο αναγνώστης το συμβάν. Για την έγκαιρη προειδοποίηση αρκούν δύο προτάσεις.',
  'incident.field.severityAssessment': 'Αρχική αξιολόγηση σοβαρότητας',
  'incident.field.severityAssessmentHint': 'Το άρθρο 23(4)(β) το ζητά στην κοινοποίηση των 72 ωρών.',
  'incident.field.impact': 'Αντίκτυπος',
  'incident.field.impactHint': 'Λειτουργική διατάραξη, οικονομική ζημία, βλάβη σε τρίτους.',
  'incident.field.affectedServices': 'Επηρεαζόμενες υπηρεσίες και αποδέκτες',
  'incident.field.indicatorsOfCompromise': 'Δείκτες παραβίασης',
  'incident.field.indicatorsOfCompromiseHint': 'Το άρθρο 23(4)(β) τους ζητά «εφόσον υπάρχουν». Αφήστε το κενό αν δεν έχετε ακόμη.',
  'incident.field.rootCause': 'Είδος απειλής ή πιθανή γενεσιουργός αιτία',
  'incident.field.rootCauseHint': 'Άρθρο 23(4)(δ)(ii).',
  'incident.field.mitigation': 'Μέτρα μετριασμού, εφαρμοσμένα και σε εξέλιξη',
  'incident.field.mitigationHint': 'Άρθρο 23(4)(δ)(iii).',
  'incident.field.crossBorderDetail': 'Διασυνοριακός αντίκτυπος',
  'incident.field.crossBorderDetailHint': 'Όπου συντρέχει — άρθρο 23(4)(α) και (δ)(iv).',
  'incident.field.ongoingStatus': 'Τρέχουσα κατάσταση χειρισμού',
  'incident.field.additionalInformation': 'Ό,τι άλλο ζητά η φόρμα της αρχής σας',
  'incident.field.additionalInformationHint':
    'Τα παραπάνω πεδία ακολουθούν όσα περιγράφει το άρθρο 23(4). Η εθνική σας φόρμα μπορεί να ζητά περισσότερα — βάλτε τα εδώ.',
  'incident.field.contactName': 'Όνομα επικοινωνίας',
  'incident.field.contactEmail': 'E-mail επικοινωνίας',
  'incident.field.contactPhone': 'Τηλέφωνο επικοινωνίας',
  'incident.field.contactPrefilled': 'Προσυμπληρώθηκε από τον λογαριασμό και τον οργανισμό σας. Αλλάξτε το αν εφημερεύει άλλος.',

  'incident.warnings.title': 'Ακόμη κενά',
  'incident.warnings.body':
    'Αυτά συνήθως ζητούνται σε αυτό το στάδιο. Δεν σας εμποδίζουν να καταγράψετε υποβολή — την 23η ώρα μια ελλιπής έγκαιρη προειδοποίηση είναι καλύτερη από μια τέλεια που δεν στάλθηκε.',
  'incident.warnings.none': 'Όλα όσα συνήθως ζητά αυτό το στάδιο έχουν συμπληρωθεί.',

  'incident.export.title': 'Εξαγωγή',
  'incident.export.body':
    'Πάρτε το στην πύλη, στο e-mail ή στη φόρμα της αρχής σας. Η έκδοση κειμένου είναι διαμορφωμένη για απευθείας επικόλληση σε φόρμα ιστού.',
  'incident.export.pdf': 'Λήψη PDF',
  'incident.export.txt': 'Λήψη κειμένου',
  'incident.export.csv': 'Χρονολόγιο (CSV)',
  'incident.export.failed': 'Η εξαγωγή δεν ήταν δυνατή.',

  'incident.submit.action': 'Καταγραφή ότι το στείλατε',
  'incident.submit.when': 'Πότε το στείλατε;',
  'incident.submit.whenHint': 'Βάλτε παλαιότερη ώρα αν το στείλατε νωρίτερα — εδώ καταγράφεται τι έγινε, όχι τι έκανε αυτή η οθόνη.',
  'incident.submit.to': 'Σε ποιον το στείλατε;',
  'incident.submit.toHint': 'Το CSIRT ή η αρμόδια αρχή, όπως την προσφωνήσατε.',
  'incident.submit.via': 'Πώς το στείλατε;',
  'incident.submit.via.PORTAL': 'Πύλη της αρχής',
  'incident.submit.via.EMAIL': 'E-mail',
  'incident.submit.via.PHONE': 'Τηλέφωνο',
  'incident.submit.via.POST': 'Ταχυδρομείο',
  'incident.submit.via.OTHER': 'Άλλο',
  'incident.submit.reference': 'Αριθμός πρωτοκόλλου που σας έδωσαν (προαιρετικό)',
  'incident.submit.lateness': 'Σημείωση για τον χρόνο (προαιρετικό)',
  'incident.submit.latenessHint':
    'Αυτό είναι μετά την προθεσμία. Αξίζει να γράψετε τώρα μια σύντομη αιτιολογία, όσο τη θυμάστε — η αρχή σας πιθανότατα θα τη ζητήσει.',
  'incident.submit.confirm': 'Καταγραφή',
  'incident.submit.failed': 'Δεν ήταν δυνατή η καταγραφή της υποβολής.',

  'incident.timeline.title': 'Χρονολόγιο',
  'incident.timeline.body':
    'Τι έγινε, πότε και από ποιον. Είναι το δικό σας αρχείο για τον χειρισμό του περιστατικού, και τυπώνεται στις αναφορές που εξάγετε.',
  'incident.timeline.add': 'Προσθήκη εγγραφής',
  'incident.timeline.when': 'Πότε συνέβη;',
  'incident.timeline.whenHint': 'Όχι πότε το πληκτρολογείτε — πότε συνέβη.',
  'incident.timeline.summary': 'Τι συνέβη',
  'incident.timeline.detail': 'Περισσότερες λεπτομέρειες (προαιρετικό)',
  'incident.timeline.save': 'Προσθήκη',
  'incident.timeline.by': 'από {{who}}',
  'incident.timeline.system': 'καταγράφηκε αυτόματα',
  'incident.timeline.empty': 'Δεν έχει καταγραφεί τίποτα ακόμη.',
  'incident.timeline.kind.DECLARED': 'Δήλωση',
  'incident.timeline.kind.NOTE': 'Σημείωση',
  'incident.timeline.kind.STATUS_CHANGED': 'Κατάσταση',
  'incident.timeline.kind.SEVERITY_CHANGED': 'Σοβαρότητα',
  'incident.timeline.kind.DETAILS_UPDATED': 'Στοιχεία',
  'incident.timeline.kind.REPORT_STARTED': 'Έναρξη προσχεδίου',
  'incident.timeline.kind.REPORT_SUBMITTED': 'Αποστολή αναφοράς',
  'incident.timeline.kind.RECIPIENTS_NOTIFIED': 'Ενημέρωση αποδεκτών',
  'incident.timeline.kind.CONTROL_LINKED': 'Σύνδεση με δικλίδα',

  'incident.controls.title': 'Τεκμήριο για τις δικλίδες σας',
  'incident.controls.body':
    'Ένα περιστατικό που χειριστήκατε αποδεικνύει ότι οι δικλίδες χειρισμού και αναφοράς περιστατικών λειτουργούν. Συνδέστε το, και το αρχείο εμφανίζεται ως τεκμήριο γι’ αυτές. Αυτό δεν δημιουργεί ποτέ κενό — το περιστατικό είναι συμβάν, όχι έλλειψη.',
  'incident.controls.add': 'Σύνδεση δικλίδας',
  'incident.controls.suggested': 'Προτεινόμενη',
  'incident.controls.none': 'Δεν έχει συνδεθεί με καμία δικλίδα ακόμη.',
  'incident.controls.remove': 'Αποσύνδεση',
  'incident.controls.chooseLabel': 'Ποια δικλίδα αποδεικνύει αυτό το περιστατικό;',

  'incident.error.INCIDENT_NOT_FOUND': 'Αυτό το περιστατικό δεν υπάρχει, ή δεν σας ανήκει.',
  'incident.error.INCIDENT_REPORT_NOT_FOUND': 'Αυτή η αναφορά δεν έχει ξεκινήσει ακόμη.',
  'incident.error.INCIDENT_REPORT_ALREADY_SUBMITTED':
    'Αυτή η αναφορά είναι ήδη καταγεγραμμένη ως σταλμένη, οπότε δεν αλλάζει πλέον.',
  'incident.error.INCIDENT_AWARE_AT_IN_FUTURE': 'Η ώρα αντίληψης είναι στο μέλλον. Πρέπει να είναι τώρα ή νωρίτερα.',
  'incident.error.ORGANIZATION_CONTROL_NOT_FOUND': 'Αυτή η δικλίδα δεν υπάρχει στη βιβλιοθήκη σας.',
};

let registered = false;

/**
 * Add both tables to the running i18n instance.
 *
 * Idempotent, and `addResources` does not overwrite a key the bundled locale
 * already carries — so the day these keys land in en.json and el.json, the
 * translated versions win and this becomes dead weight rather than a conflict.
 */
export function registerIncidentTranslations() {
  if (registered) return;
  registered = true;
  for (const [lng, table] of [['en', INCIDENT_STRINGS], ['el', INCIDENT_STRINGS_EL]] as const) {
    i18n.addResources(lng, 'translation', table as Record<string, string>);
  }
}
registerIncidentTranslations();

export function useIncidentT() {
  const { t, i18n: instance } = useTranslation();

  const tx = useCallback(
    (key: IncidentKey, options?: Record<string, unknown>): string =>
      t(key, { defaultValue: INCIDENT_STRINGS[key], ...options }) as string,
    [t],
  );

  /** Plural-aware: i18next picks the suffix, we pick the inline English fallback. */
  const txn = useCallback(
    (key: IncidentPluralKey, count: number): string =>
      t(key, {
        count,
        defaultValue: INCIDENT_STRINGS[`${key}_${count === 1 ? 'one' : 'other'}` as IncidentKey],
      }) as string,
    [t],
  );

  const formatDateTime = useCallback(
    (value: string | Date | null | undefined): string => {
      if (!value) return '—';
      return new Intl.DateTimeFormat(instance.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value));
    },
    [instance.language],
  );

  return { tx, txn, formatDateTime };
}
